import { CommonModule } from '@angular/common';
import {
  Component,
  Input,
  AfterViewInit,
  OnChanges,
  SimpleChanges,
  ElementRef,
  ViewChild,
  OnDestroy,
  inject
} from '@angular/core';

import * as echarts from 'echarts';
import { applyRoleVisibilityToOption } from '../../utils/chart-role-visibility.utils';
import { Subject, Subscription, takeUntil } from 'rxjs';
import { ConfigService } from '../../shared-services/config.service';
import { GlobalFilterService } from '../../shared-services/global-filter-service';
import { ChartFilterHandlerService } from '../../shared-services/chart-filter-handler.service';
import { ResponsiveLayoutService } from '../../shared-services/responsive-layout.service';
import { ThemeService } from '../../shared-services/theme.service';
import { buildCategoryFilterData, buildLegendSyncData, matchValue, MatchMode } from '../../utils/filter.utils';

import { CapitalCasePipe } from '../../pipes/capital-case.pipe';

@Component({
  selector: 'app-multi-level-comparison-chart',
  standalone: true,
  imports: [CommonModule, CapitalCasePipe, CapitalCasePipe],
  templateUrl: './multi-level-comparison-chart.component.html',
  styleUrls: ['./multi-level-comparison-chart.component.scss']
})
export class MultiLevelComparisonChartComponent
  implements AfterViewInit, OnChanges, OnDestroy {

  private destroy$ = new Subject<void>();
  private lastFilters: any = null;
  protected responsiveLayoutService = inject(ResponsiveLayoutService);
  private themeService = inject(ThemeService);
  protected reflowSub?: Subscription;
  private themeSub?: Subscription;

  // Cross-filtering state
  selectedCategories: string[] = [];
  selectedLegends: string[] = [];
  private legendSelectedState: Record<string, boolean> = {};
  // Store engine labels and boundaries for click handling
  private visEngineLabels: string[] = [];
  private visYearLabels: string[] = [];

  @ViewChild('chartRef', { static: false }) chartRef!: ElementRef;

  chart!: echarts.ECharts;

  /** API response */
  @Input() response: any;

  // Support the shared `data` payload set by ChartRendererService
  private _data: any;
  @Input()
  set data(value: any) {
    this._data = value;
    this.onDataAssigned();
  }
  get data(): any {
    return this._data;
  }

  /** Configurable keys (can also be read from this.data) */
  @Input() engineKey!: string;
  @Input() yearKey!: string;
  @Input() valueKey!: string;
  @Input() startYearKey!: string;
  @Input() endYearKey!: string;
  @Input() changeKey!: string;

  // ── Custom scrollbar state (matching mixed-stackbar) ──────────────
  scrollOffset = 0;
  maxVisibleEngines = 5;
  totalEngineCount = 0;
  private isScrollbarDragging = false;
  private scrollbarStartX = 0;
  private scrollStartOffset = 0;
  private dragHandler: ((e: MouseEvent) => void) | null = null;
  private upHandler: (() => void) | null = null;

  // ── function toggle state (configurable via JSON)
  originalFunctionName: string | null = null;
  altFunctions: string[] = [];
  currentAltIndex = -1;

  // changeKey toggle support
  originalChangeKey: string | null = null;
  altChangeKeys: string[] = [];

  /** Default color palette for engine groups */
  private readonly defaultPalette = [
    '#040505', '#DC143C', '#696969', '#4169E1',
    '#DAA520', '#CD5C5C', '#9370DB', '#2F4F4F',
    '#FF8C00', '#8B0000', '#556B2F', '#6495ED',
    '#B8860B', '#483D8B', '#00CED1', '#9400D3',
    '#FF6347', '#4682B4', '#32CD32', '#FF1493',
    '#00FA9A', '#FFD700', '#8A2BE2', '#A0522D'
  ];

  constructor(
    private configService: ConfigService,
    private globalFilterService: GlobalFilterService,
    private chartFilterHandler: ChartFilterHandlerService
  ) {
    this.globalFilterService.filter$
      .pipe(takeUntil(this.destroy$))
      .subscribe((filters) => {
        this.lastFilters = filters;

        if (!this.chartFilterHandler.shouldReload(filters, this._data?.widgetId)) {
          // Handle legendSync (visual dimming from other charts)
          if (filters?.legendSync) {
            this.handleExternalLegendSync(filters);
          }
          return;
        }

        // Handle legendSync + reload
        if (filters?.legendSync) {
          this.handleExternalLegendSync(filters);
        }

        // Only clear selection on global dropdown change (no widgetId)
        if (!filters?.widgetId) {
          this.selectedCategories = [];
          this.selectedLegends = [];
        }

        if (Object.keys(filters || {}).length !== 0) {
          this.loadChartData(filters);
        }
      });
  }

  ngAfterViewInit(): void {
    this.chart = echarts.init(this.chartRef.nativeElement, 'dvm');
    this.renderChart();

    this.reflowSub = this.responsiveLayoutService.reflow$.subscribe(() => {
      this.chart?.resize();
    });

    // Re-render with fresh theme tokens when the user switches theme at runtime.
    // Dispose + re-init so ECharts picks up the updated 'dvm' registered theme.
    this.themeSub = this.themeService.theme$.subscribe(() => {
      if (!this.response || !this.chart || !this.chartRef?.nativeElement) return;
      this.chart.dispose();
      this.chart = echarts.init(this.chartRef.nativeElement, 'dvm');
      this.renderChart();
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.reflowSub?.unsubscribe();
    this.themeSub?.unsubscribe();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.chart && changes['response']) {
      this.renderChart();
    }
  }

  protected onDataAssigned(): void {
    if (!this.data) return;

    // Capture original function name and any alternate functions declared in JSON
    this.originalFunctionName = this.data.functionName || null;
    const altCfg = this.data.altFunctionName ?? this.data.chartConfig?.altFunctionName;
    if (altCfg) {
      this.altFunctions = Array.isArray(altCfg) ? altCfg : [altCfg];
      this.currentAltIndex = -1;
    } else {
      this.altFunctions = [];
      this.currentAltIndex = -1;
    }

    // Capture original changeKey and alternate changeKey(s) (optional)
    this.originalChangeKey = this.data.changeKey ?? null;
    const altChangeCfg = this.data.altChangeKey ?? this.data.chartConfig?.altChangeKey;
    if (altChangeCfg) {
      this.altChangeKeys = Array.isArray(altChangeCfg) ? altChangeCfg : [altChangeCfg];
    } else {
      this.altChangeKeys = [];
    }

    if (this.data.apiUrl) {
      this.loadChartData();
      return;
    }

    if (this.data.response) {
      this.response = this.data.response;
      if (this.chart) this.renderChart();
    }
  }


  protected loadChartData(filters?: any): void {
    console.log('MultiLevelComparisonChartComponent: loadChartData called, filters:', filters, 'data:', this.data);
    try {
      if ((!this.data.functionName || this.data.functionName === '') && Array.isArray(this.data?.endpoints) && this.data.endpoints.length) {
        this.data.functionName = this.data.endpoints[0].functionName;
      }
      if ((!this.data.functionName || this.data.functionName === '') && this.data?.requestData?.functionName) {
        this.data.functionName = this.data.requestData.functionName;
      }
    } catch (e) {
      // ignore
    }

    console.log('MultiLevelComparisonChartComponent: using functionName=', this.data?.functionName);

    this.configService.getConfig(
      this.data.apiUrl,
      this.data,
      { ...filters, useQueryParams: this.data?.useQueryParams || false },
      this.data.method
    ).pipe(takeUntil(this.destroy$)).subscribe({
      next: (res: any) => {
        this.response = res;
        if (this.chart) this.renderChart();
      },
      error: () => {
        this.response = null;
      }
    });
  }

  // ── helpers ────────────────────────────────────────────────────────────

  /** Resolve a key from JSON config (this.data). No hardcoded fallbacks. */
  protected key(field: string): string {
    const val = this.data?.[field];
    if (!val) {
      console.warn(`[MultiLevelComparisonChart] Missing key '${field}' in JSON config.`);
    }
    return val || '';
  }

  /** Walk a dot-separated path into an object */
  protected getByPath(obj: any, path: string): any {
    return path.split('.').reduce((cur: any, part: string) => {
      if (cur === null || cur === undefined) return undefined;
      return /^\d+$/.test(part) ? cur[Number(part)] : cur[part];
    }, obj);
  }

  /** Try multiple paths and return the first hit */
  protected tryPaths(obj: any, paths: string[]): any {
    for (const p of paths) {
      const val = this.getByPath(obj, p);
      if (val !== undefined) return val;
    }
    return undefined;
  }

  // ── main render ────────────────────────────────────────────────────────

  renderChart(): void {
    // Ensure chart DOM is available (template uses *ngIf="data")
    if (!this.chart && this.chartRef && this.chartRef.nativeElement) {
      this.chart = echarts.init(this.chartRef.nativeElement, 'dvm');
    }

    if (!this.response) return;

    const theme = this.themeService.resolve(this.data?.themeOverrides);

    // Resolve all keys from JSON config (this.data)
    const engineField   = this.key('engineKey');
    const yearField     = this.key('yearKey');
    const valueField    = this.key('valueKey');
    const startYrField  = this.key('startYearKey');
    const endYrField    = this.key('endYearKey');
    const changeField   = this.key('changeKey');

    // Locate bar / compute arrays in (possibly unwrapped) response
    const barData: any[] = (Array.isArray(this.response) ? this.response : null)
      || this.tryPaths(this.response, ['bar', 'responseList.0.data.0.bar', 'data.0.bar'])
      || [];

    const computeData: any[] = this.tryPaths(this.response, [
      'compute', 'responseList.0.data.0.compute', 'data.0.compute'
    ]) || [];

    if (!barData.length) return;

    // ── group by engine, build per-engine year lists ──────────────────

    const engineOrder: string[] = [];
    const engineYearsMap = new Map<string, number[]>();
    const engineValuesMap = new Map<string, (number | null)[]>();

    for (const row of barData) {
      const eng: string = row[engineField];
      const yr: number  = row[yearField];
      const val: number = row[valueField];

      if (!engineYearsMap.has(eng)) {
        engineOrder.push(eng);
        engineYearsMap.set(eng, []);
        engineValuesMap.set(eng, []);
      }

      const years  = engineYearsMap.get(eng)!;
      const values = engineValuesMap.get(eng)!;

      // Insert maintaining year sort order
      let idx = years.findIndex(y => y > yr);
      if (idx === -1) idx = years.length;
      years.splice(idx, 0, yr);
      values.splice(idx, 0, val);
    }

    // Sort engines by displacement (from data) or alphabetically
    engineOrder.sort((a, b) => {
      const dispA = barData.find((r: any) => r[engineField] === a)?.displacement ?? 0;
      const dispB = barData.find((r: any) => r[engineField] === b)?.displacement ?? 0;
      return dispA - dispB || a.localeCompare(b);
    });

    // ── build flat axis data ─────────────────────────────────────────

    const yearLabels: string[]  = [];   // year labels on first x-axis
    const engineLabels: string[] = []; // engine labels on second x-axis
    const barValues: (number | null)[] = [];
    const barColors: string[] = [];

    // Track index boundaries per engine for the second axis
    const engineBoundaries: { name: string; startIdx: number; endIdx: number }[] = [];

    const palette = this.data?.chartConfig?.barColors || this.defaultPalette;

    let globalIdx = 0;

    engineOrder.forEach((eng, engIdx) => {
      const years  = engineYearsMap.get(eng)!;
      const values = engineValuesMap.get(eng)!;
      const color  = palette[engIdx % palette.length];

      const startIdx = globalIdx;

      years.forEach((yr, i) => {
        yearLabels.push(String(yr).slice(-2));   // show last 2 digits  e.g. "18"
        engineLabels.push(eng);
        barValues.push(values[i]);
        barColors.push(color);
        globalIdx++;
      });

      engineBoundaries.push({ name: eng, startIdx, endIdx: globalIdx - 1 });
    });

    // ── build comparison lines from compute ──────────────────────────

    const comparisonData: any[] = [];

    for (const row of computeData) {
      const eng      = row[engineField];
      const startYr  = row[startYrField];
      const endYr    = row[endYrField];
      const change   = row[changeField];

      const boundary = engineBoundaries.find(b => b.name === eng);
      if (!boundary) continue;

      const years = engineYearsMap.get(eng)!;
      const startLocalIdx = years.indexOf(startYr);
      const endLocalIdx   = years.indexOf(endYr);
      if (startLocalIdx === -1 || endLocalIdx === -1) continue;

      const startGlobal = boundary.startIdx + startLocalIdx;
      const endGlobal   = boundary.startIdx + endLocalIdx;

      const startVal = barValues[startGlobal];
      const endVal   = barValues[endGlobal];

      // Find the max bar value across the ENTIRE span so the line clears all bars
      let maxValInSpan = 0;
      const lo = Math.min(startGlobal, endGlobal);
      const hi = Math.max(startGlobal, endGlobal);
      for (let k = lo; k <= hi; k++) {
        maxValInSpan = Math.max(maxValInSpan, barValues[k] ?? 0);
      }

      comparisonData.push({
        value: [startGlobal, startVal, endGlobal, endVal, change, maxValInSpan]
      });
    }

    // ── Slice to visible engines based on scrollOffset ────────────

    this.totalEngineCount = engineOrder.length;
    this.maxVisibleEngines = this.data?.chartConfig?.maxVisibleEngines || 5;
    const safeOffset = Math.min(this.scrollOffset, Math.max(0, engineOrder.length - this.maxVisibleEngines));
    this.scrollOffset = safeOffset;

    const visStart = safeOffset;
    const visEnd   = Math.min(safeOffset + this.maxVisibleEngines, engineOrder.length);
    const visibleBoundaries = engineBoundaries.slice(visStart, visEnd);

    // Re-index the visible slice
    const sliceFrom = visibleBoundaries.length ? visibleBoundaries[0].startIdx : 0;
    const sliceTo   = visibleBoundaries.length ? visibleBoundaries[visibleBoundaries.length - 1].endIdx + 1 : 0;

    const visYearLabels  = yearLabels.slice(sliceFrom, sliceTo);
    const visEngineLabels = engineLabels.slice(sliceFrom, sliceTo);
    const visBarValues   = barValues.slice(sliceFrom, sliceTo);
    const visBarColors   = barColors.slice(sliceFrom, sliceTo);

    // Filter comparison data to only visible range and re-index
    const visComparisonData = comparisonData
      .filter((d: any) => d.value[0] >= sliceFrom && d.value[2] < sliceTo)
      .map((d: any) => ({
        value: [
          d.value[0] - sliceFrom,
          d.value[1],
          d.value[2] - sliceFrom,
          d.value[3],
          d.value[4],
          d.value[5]
        ]
      }));

    // ── second x-axis: show engine name once per group ───────────────

    const engineAxisLabels: string[] = new Array(visYearLabels.length).fill('');
    visibleBoundaries.forEach(b => {
      const adjStart = b.startIdx - sliceFrom;
      const adjEnd   = b.endIdx - sliceFrom;
      const mid = Math.floor((adjStart + adjEnd) / 2);
      engineAxisLabels[mid] = b.name;
    });

    // ── echarts option ───────────────────────────────────────────────

    // Font scale factor for large / TV screens
    const fs = this.responsiveLayoutService.getFontScale();
    const s = (px: number) => Math.round(px * fs); // scale helper

    const seriesField = this.key('seriesKey');
    const engineFieldLabel = this.key('engineKey');
const barSeriesName =
  this.data?.chartConfig?.barLabel ||
  this.data?.seriesLabel ||
  this.data?.category ||
  valueField ||
  'Sales';
    const comparisonSeriesName = this.data?.chartConfig?.comparisonLabel || 'Comparison';

    const option: echarts.EChartsOption = {

      legend: {
        show: true,
        top: s(8),
        left: 'center',
        data: [barSeriesName, comparisonSeriesName],
        textStyle: { fontSize: s(12) }
      },

      tooltip: {
        trigger: 'axis',
        backgroundColor: theme.tooltip?.bg || '#fff',
        borderColor: theme.tooltip?.borderColor || '#ccc',
        textStyle: {
          color: theme.tooltip?.textColor || '#333',
          fontSize: theme.tooltip?.fontSize || s(12),
        },
        formatter: (params: any) => {
          if (!Array.isArray(params) || !params.length) return '';
          const idx = params[0].dataIndex;
          const eng = visEngineLabels[idx];
          const yr  = visYearLabels[idx];
          const val = visBarValues[idx];
          return `${eng} · 20${yr}<br/>` +
                 `${valueField}: <b>${val != null ? Math.round(val).toLocaleString() : '–'}</b>`;
        }
      },

      grid: {
        left: '5%',
        right: '3%',
        top: s(50),
        bottom: s(80),
        containLabel: true
      },

      xAxis: [
        // Year axis (inner)
        {
          type: 'category',
          data: visYearLabels,
          triggerEvent: true,
          axisTick: { alignWithLabel: true, interval: 0 },
          axisLabel: {
            interval: 0,
            fontSize: s(10),
            rotate: 0
          },
          axisLine: { show: true }
        },
        // Engine axis (outer, below year axis)
        {
          type: 'category',
          data: engineAxisLabels,
          triggerEvent: true,
          position: 'bottom',
          offset: s(30),
          axisLine: { show: false },
          axisTick: { show: false },
          axisLabel: {
            interval: 0,
            fontWeight: 'bold',
            fontSize: s(11)
          }
        }
      ],

      yAxis: {
        type: 'value',
        axisLabel: {
          fontSize: s(10),
          formatter: (v: number) => v >= 1000 ? (v / 1000).toFixed(0) + 'k' : String(v)
        },
        splitLine: { show: false }
      },

      series: [

        // ── BAR SERIES ──────────────────────────────────────────────
        {
          name: barSeriesName,
          type: 'bar',
          barWidth: s(20),
          barCategoryGap: '20%',
          data: visBarValues.map((v, i) => ({
            value: v,
            itemStyle: { color: visBarColors[i] }
          })),
          label: {
              show: true,
              position: 'inside',
              fontSize: s(9),
              color: '#fff',
              formatter: (p: any) => {
                const v = p.value;
                if (v == null || isNaN(v)) return '';
                const abs = Math.abs(v);
                if (abs >= 1000000) return Math.round(v / 1000000).toString() + 'm';
                if (abs >= 1000) return Math.round(v / 1000).toString() + 'k';
                return Math.round(v).toString();
              }
          }
        },

        // ── COMPARISON LINES with BUBBLES (custom series) ────────────
        {
          name: comparisonSeriesName,
          type: 'custom',
          renderItem: (params: any, api: any) => {
            const x1 = api.value(0);
            const y1 = api.value(1);
            const x2 = api.value(2);
            const y2 = api.value(3);
            const pct = api.value(4);

            const maxValInSpan = api.value(5);

            const start = api.coord([x1, y1]);
            const end   = api.coord([x2, y2]);

            // Use the tallest bar in the full span to position the line
            const tallestBarTop = api.coord([x1, maxValInSpan])[1];
            // Place line well above tallest bar + value labels
            const lineY = tallestBarTop - s(45);

            const midX = (start[0] + end[0]) / 2;

            const pctText = pct != null
              ? (pct > 0 ? '+' : '') + Math.round(pct) + '%'
              : '';

            // Measure bubble size based on text length — scaled
            const bubbleRadiusX = Math.max(s(22), pctText.length * s(5) + s(8));
            const bubbleRadiusY = s(12);
            const arrowSize = s(4);
            const stemGap = s(4);
            const lw = Math.max(1, s(2));

            return {
              type: 'group',
              children: [
                // Straight horizontal line
                {
                  type: 'line',
                  shape: { x1: start[0], y1: lineY, x2: end[0], y2: lineY },
                  style: { stroke: '#555', lineWidth: lw }
                },
                // Left vertical stem (down-arrow from line to bar top)
                {
                  type: 'line',
                  shape: { x1: start[0], y1: lineY, x2: start[0], y2: start[1] - stemGap },
                  style: { stroke: '#555', lineWidth: lw }
                },
                // Left arrow head
                {
                  type: 'polygon',
                  shape: {
                    points: [
                      [start[0], start[1] - s(2)],
                      [start[0] - arrowSize, start[1] - arrowSize * 2],
                      [start[0] + arrowSize, start[1] - arrowSize * 2]
                    ]
                  },
                  style: { fill: '#555' }
                },
                // Right vertical stem (down-arrow from line to bar top)
                {
                  type: 'line',
                  shape: { x1: end[0], y1: lineY, x2: end[0], y2: end[1] - stemGap },
                  style: { stroke: '#555', lineWidth: lw }
                },
                // Right arrow head
                {
                  type: 'polygon',
                  shape: {
                    points: [
                      [end[0], end[1] - s(2)],
                      [end[0] - arrowSize, end[1] - arrowSize * 2],
                      [end[0] + arrowSize, end[1] - arrowSize * 2]
                    ]
                  },
                  style: { fill: '#555' }
                },
                // Bubble (pill / rounded rect) on the line
                {
                  type: 'rect',
                  shape: {
                    x: midX - bubbleRadiusX,
                    y: lineY - bubbleRadiusY,
                    width: bubbleRadiusX * 2,
                    height: bubbleRadiusY * 2,
                    r: bubbleRadiusY   // fully rounded ends → pill shape
                  },
                  style: { fill: '#555' }
                },
                // Percentage text inside the bubble
                {
                  type: 'text',
                  style: {
                    text: pctText,
                    x: midX,
                    y: lineY,
                    fill: '#fff',
                    fontSize: s(11),
                    fontWeight: 'bold' as any,
                    align: 'center' as any,
                    verticalAlign: 'middle' as any
                  }
                }
              ]
            };
          },
          encode: { x: [0, 2], y: [1, 3] },
          data: visComparisonData,
          z: 10
        }

      ]
    };

    // Store labels for click handling
    this.visEngineLabels = visEngineLabels;
    this.visYearLabels = visYearLabels;

    applyRoleVisibilityToOption(option, this.data);

    this.chart.setOption(option, true);

    // Setup click events for cross-filtering
    this.setupChartClickEvent();

    // Re-apply legend selection state after re-render (show/hide)
    if (Object.keys(this.legendSelectedState).length > 0) {
      this.chart.setOption({ legend: { selected: this.legendSelectedState } });
    }

    // Re-apply active visual selection after re-render
    if (this.selectedCategories.length > 0) {
      this.highlightCategories(this.selectedCategories);
    }
  }

  // ==================== CROSS-FILTERING ====================

  protected setupChartClickEvent(): void {
    this.chart.off('click');
    this.chart.on('click', (params: any) => {
      if (params.componentType === 'xAxis') {
        const axisIndex = params.xAxisIndex ?? params.axisIndex ?? 0;
        if (axisIndex === 1) {
          // Outer x-axis (engine names) → filter by labelKey ("engine_c")
          if (params.value && this._data?.enableLabelClick) {
            this.handleLabelClick(params.value);
          }
        } else if (axisIndex === 0) {
          // Inner x-axis (year labels like "18") → filter by doublexAxisKey ("year") + labelKey ("engine_c")
          if (params.value && this._data?.enableDoubleXAxis && this._data?.enableXAxis2Click) {
            const clickedYear = `20${params.value}`;
            const dataIndex = params.dataIndex ?? 0;
            const engineName = this.visEngineLabels[dataIndex] || '';

            // Append engine name to engine_c using multi-select logic
            const labelKey = this._data?.labelKey;
            const listener = this._data?.labelListener || this._data?.filterListener || this._data?.filterListner;
            if (labelKey && engineName) {
              // Get current engine_c values and append (multi-select)
              const currentFilters = this.globalFilterService.getCurrentFilters();
              const existingValues: string[] = currentFilters?.requestParams?.[labelKey] || [];
              let updatedValues: string[];
              if (this._data?.isMultiSelect) {
                // Toggle: add if not present, remove if already there
                if (existingValues.includes(engineName)) {
                  updatedValues = existingValues.filter((v: string) => v !== engineName);
                } else {
                  updatedValues = [...existingValues, engineName];
                }
              } else {
                updatedValues = [engineName];
              }
              if (updatedValues.length > 0) {
                this.globalFilterService.updateFilterSelection(
                  labelKey, updatedValues, false, this._data.widgetId, false, listener,
                );
              } else {
                this.globalFilterService.deleteKey(labelKey, false);
              }
            }

            // Then set year filter (with emit so listeners reload)
            this.chartFilterHandler.handleDoubleXAxisClick(this._data, clickedYear);
            this.loadChartData(this.globalFilterService.getCurrentFilters());
          }
        }
        return;
      }

      // Bar click → filter by engine name using labelKey ("engine_c")
      if (params.componentType === 'series' && params.dataIndex !== undefined) {
        const engineName = this.visEngineLabels[params.dataIndex];
        if (engineName && this._data?.enableLabelClick) {
          this.handleLabelClick(engineName);
        }
        return;
      }
    });

    // Legend click → let ECharts handle show/hide, just trigger API call
    this.chart.off('legendselectchanged');
    this.chart.on('legendselectchanged', (params: any) => {
      if (!this._data?.enableLegendClick) return;

      // Save legend selection state so it persists after re-render
      this.legendSelectedState = { ...params.selected };

      const legendKey = this._data?.legendKey || this._data?.filterKey;
      const listenerConfig =
        this._data?.legendListener ||
        this._data?.filterListener ||
        this._data?.filterListner;

      if (!legendKey) return;

      // Check which series are still selected (visible)
      const visibleSeries = Object.entries(params.selected)
        .filter(([_, v]) => v)
        .map(([k]) => k);
      const hiddenSeries = Object.entries(params.selected)
        .filter(([_, v]) => !v)
        .map(([k]) => k);

      if (hiddenSeries.length > 0) {
        this.globalFilterService.updateFilterSelection(
          legendKey, visibleSeries, false, this._data.widgetId, true, listenerConfig,
        );
      } else {
        this.globalFilterService.resetFilterSelection(legendKey, false, listenerConfig);
      }
      this.loadChartData(this.globalFilterService.getCurrentFilters());
    });
  }

  /**
   * Handle label click with multi-select toggle.
   */
  protected handleLabelClick(clickedValue: string): void {
    if (!clickedValue || !this._data?.enableLabelClick) return;
    const clickedStr = String(clickedValue);

    if (this._data?.isMultiSelect) {
      const idx = this.selectedCategories.findIndex(c => String(c) === clickedStr);
      if (idx === -1) {
        this.selectedCategories = [...this.selectedCategories, clickedStr];
      } else {
        this.selectedCategories = this.selectedCategories.filter(c => String(c) !== clickedStr);
      }
    } else {
      if (this.selectedCategories.length === 1 && String(this.selectedCategories[0]) === clickedStr) {
        this.selectedCategories = [];
      } else {
        this.selectedCategories = [clickedStr];
      }
    }

    if (this.selectedCategories.length === 0) {
      this.broadcastCategoryFilter([]);
    } else {
      this.broadcastCategoryFilter(this.selectedCategories);
    }

    // Reload self
    this.loadChartData(this.globalFilterService.getCurrentFilters());
  }

  /**
   * Broadcast category selection: legendSync + filter update.
   */
  protected broadcastCategoryFilter(categories: string[]): void {
    const listenerConfig =
      this._data?.labelListener ||
      this._data?.legendListener ||
      this._data?.filterListener ||
      this._data?.filterListner;

    const labelKey = this._data?.labelKey || this._data?.filterKey;

    // Step 1: legendSync for visual dimming
    const legendSyncData = {
      ...this.globalFilterService.getCurrentFilters(),
      ...buildCategoryFilterData(categories, this._data.widgetId, listenerConfig, labelKey),
    };
    (this.globalFilterService as any).filterSubject.next(legendSyncData);

    // Step 2: regular filter update for API calls
    if (labelKey) {
      if (categories.length > 0) {
        this.globalFilterService.updateFilterSelection(
          labelKey, categories, false, this._data.widgetId, true, listenerConfig,
        );
      } else {
        this.globalFilterService.resetFilterSelection(labelKey, false, listenerConfig);
      }
    }
  }

  /**
   * Handle incoming legendSync from another chart.
   */
  protected handleExternalLegendSync(filters: any): void {
    if (filters.sourceWidgetId === this._data?.widgetId) return;
    if (!this.chart) return;

    if (filters.selectedLegends?.length === 0 && filters.selectedIndices?.length === 0) {
      this.selectedCategories = [];
      this.resetHighlight();
      return;
    }

    if (filters.selectedLegends?.length > 0) {
      const matchMode = (this._data?.chartConfig?.categoryMatchMode || 'contains') as MatchMode;
      // Try matching against engine labels
      const uniqueEngines = [...new Set(this.visEngineLabels)];
      const matchingEngines = uniqueEngines.filter((eng: string) =>
        filters.selectedLegends.some((legend: string) => matchValue(eng, legend, matchMode))
      );
      if (matchingEngines.length > 0) {
        this.highlightCategories(matchingEngines);
        return;
      }
      // Try matching year labels
      const uniqueYears = [...new Set(this.visYearLabels)];
      const matchingYears = uniqueYears.filter((yr: string) =>
        filters.selectedLegends.some((legend: string) => matchValue(yr, legend, matchMode) || matchValue(`20${yr}`, legend, matchMode))
      );
      if (matchingYears.length > 0) {
        this.highlightByYears(matchingYears);
      }
    }
  }

  // ==================== VISUAL HIGHLIGHTING ====================

  /**
   * Dim all bars except those whose engine name matches.
   */
  protected highlightCategories(engineNames: string[]): void {
    if (!this.chart) return;
    const option = this.chart.getOption() as any;
    if (!option?.series) return;

    const updatedSeries = option.series.map((s: any) => {
      const seriesType = Array.isArray(s.type) ? s.type[0] : s.type;
      if (seriesType !== 'bar') return s;
      if (!s.data) return s;
      const dataArr = Array.isArray(s.data[0]) ? s.data[0] : s.data;
      const newData = dataArr.map((val: any, idx: number) => {
        const engineName = this.visEngineLabels[idx] || '';
        const isMatch = engineNames.some(name => String(name) === String(engineName));
        const opacity = isMatch ? 1 : 0.2;
        if (typeof val === 'object' && val !== null) {
          return { ...val, itemStyle: { ...(val.itemStyle || {}), opacity } };
        }
        return { value: val, itemStyle: { opacity } };
      });
      return { ...s, data: newData };
    });

    this.chart.setOption({ series: updatedSeries });
  }

  /**
   * Dim all bars except those whose year label matches.
   */
  protected highlightByYears(years: string[]): void {
    if (!this.chart) return;
    const option = this.chart.getOption() as any;
    if (!option?.series) return;

    const updatedSeries = option.series.map((s: any) => {
      const seriesType = Array.isArray(s.type) ? s.type[0] : s.type;
      if (seriesType !== 'bar') return s;
      if (!s.data) return s;
      const dataArr = Array.isArray(s.data[0]) ? s.data[0] : s.data;
      const newData = dataArr.map((val: any, idx: number) => {
        const yearLabel = this.visYearLabels[idx] || '';
        const isMatch = years.some(yr => String(yr) === String(yearLabel));
        const opacity = isMatch ? 1 : 0.2;
        if (typeof val === 'object' && val !== null) {
          return { ...val, itemStyle: { ...(val.itemStyle || {}), opacity } };
        }
        return { value: val, itemStyle: { opacity } };
      });
      return { ...s, data: newData };
    });

    this.chart.setOption({ series: updatedSeries });
  }

  /**
   * Reset all visual highlighting.
   */
  protected resetHighlight(): void {
    if (!this.chart) return;
    const option = this.chart.getOption() as any;
    if (!option?.series) return;

    const updatedSeries = option.series.map((s: any) => {
      const seriesType = Array.isArray(s.type) ? s.type[0] : s.type;
      if (seriesType !== 'bar') return s;
      if (!s.data) return s;
      const dataArr = Array.isArray(s.data[0]) ? s.data[0] : s.data;
      const newData = dataArr.map((val: any) => {
        if (typeof val === 'object' && val !== null) {
          return { ...val, itemStyle: { ...(val.itemStyle || {}), opacity: 1 } };
        }
        return { value: val, itemStyle: { opacity: 1 } };
      });
      return { ...s, data: newData };
    });

    this.chart.setOption({ series: updatedSeries });
  }

  // Toggle between original functionName and configured alternate(s)
  toggleFunctionName(): void {
    if (!this.data) return;

    // ensure altFunctions populated
    if (!this.altFunctions.length) {
      const altCfg = this.data.altFunctionName ?? this.data.chartConfig?.altFunctionName;
      if (altCfg) this.altFunctions = Array.isArray(altCfg) ? altCfg : [altCfg];
    }

    if (!this.originalFunctionName) {
      this.originalFunctionName = this.data.functionName || null;
    }

    if (!this.altFunctions.length) return;

    // also ensure altChangeKeys populated
    if (!this.altChangeKeys.length) {
      const altChangeCfg = this.data.altChangeKey ?? this.data.chartConfig?.altChangeKey;
      if (altChangeCfg) this.altChangeKeys = Array.isArray(altChangeCfg) ? altChangeCfg : [altChangeCfg];
    }

    const cycleLen = this.altFunctions.length + 1; // includes original as last
    const next = (this.currentAltIndex + 1) % cycleLen;
    if (next === this.altFunctions.length) {
      // reset to original
      this.data.functionName = this.originalFunctionName || '';
      // restore original changeKey
      if (this.originalChangeKey !== null) this.data.changeKey = this.originalChangeKey;
      this.currentAltIndex = -1;
    } else {
      this.data.functionName = this.altFunctions[next];
      // set changeKey if altChangeKeys provided
      if (this.altChangeKeys.length) {
        // if a single altChangeKey provided, reuse it; otherwise match index
        const changeKeyToSet = this.altChangeKeys.length === 1
          ? this.altChangeKeys[0]
          : (this.altChangeKeys[next] ?? this.originalChangeKey ?? '');
        if (changeKeyToSet) this.data.changeKey = changeKeyToSet;
      }
      this.currentAltIndex = next;
    }

    // reload data with the new function name / changeKey, preserving current filters
    this.loadChartData(this.lastFilters || undefined);
  }

  // ── Custom scrollbar helpers (matching mixed-stackbar) ─────────────

  getScrollbarThumbWidth(): number {
    if (this.totalEngineCount <= 0) return 0;
    const containerWidth = this.chartRef?.nativeElement?.clientWidth || 600;
    const ratio = Math.min(1, this.maxVisibleEngines / this.totalEngineCount);
    return Math.max(30, Math.round(containerWidth * ratio));
  }

  getScrollbarThumbLeft(): number {
    const containerWidth = this.chartRef?.nativeElement?.clientWidth || 600;
    const maxScroll = Math.max(0, this.totalEngineCount - this.maxVisibleEngines);
    if (maxScroll <= 0) return 0;
    const thumbWidth = this.getScrollbarThumbWidth();
    const maxThumbTravel = containerWidth - thumbWidth;
    return Math.floor((this.scrollOffset / maxScroll) * maxThumbTravel);
  }

  onScrollbarMouseDown(event: MouseEvent): void {
    event.preventDefault();
    if (this.totalEngineCount <= this.maxVisibleEngines) return;

    this.isScrollbarDragging = true;
    this.scrollbarStartX = event.clientX;
    this.scrollStartOffset = this.scrollOffset;

    this.dragHandler = (e: MouseEvent) => this.onScrollbarDrag(e);
    this.upHandler = () => this.onScrollbarMouseUp();
    document.addEventListener('mousemove', this.dragHandler!);
    document.addEventListener('mouseup', this.upHandler!);
  }

  protected onScrollbarDrag(event: MouseEvent): void {
    if (!this.isScrollbarDragging) return;
    const containerWidth = this.chartRef?.nativeElement?.clientWidth || 600;
    const maxScroll = Math.max(0, this.totalEngineCount - this.maxVisibleEngines);
    const thumbWidth = this.getScrollbarThumbWidth();
    const maxThumbTravel = containerWidth - thumbWidth;
    if (maxThumbTravel <= 0) return;

    const deltaX = event.clientX - this.scrollbarStartX;
    const startThumbLeft = (this.scrollStartOffset / maxScroll) * maxThumbTravel;
    const newThumbLeft = startThumbLeft + deltaX;
    const clampedLeft = Math.max(0, Math.min(newThumbLeft, maxThumbTravel));
    const newOffset = Math.round((clampedLeft / maxThumbTravel) * maxScroll);

    if (newOffset !== this.scrollOffset && newOffset >= 0 && newOffset <= maxScroll) {
      this.scrollOffset = newOffset;
      this.renderChart();
    }
  }

  protected onScrollbarMouseUp(): void {
    this.isScrollbarDragging = false;
    if (this.dragHandler) {
      document.removeEventListener('mousemove', this.dragHandler);
      this.dragHandler = null;
    }
    if (this.upHandler) {
      document.removeEventListener('mouseup', this.upHandler);
      this.upHandler = null;
    }
  }
}